import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Close as CloseIcon, 
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { investmentAPI } from '../../api/investmentAPI';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import TickerDetailView from '../TickerDetailView';
import { Screener } from '../../types/investment';
import { useAuth } from '../../contexts/AuthContext';
import { EnhancedWatchlistTable } from './EnhancedWatchlistTable';
import { useProfile } from '../../contexts/ProfileContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';

interface ScreenerManagerProps {
  profileId: string;
  profile: {
    id: string;
    name: string;
    investment_profile_template: string;
  };
}

interface PrecannedScreener {
  query_name: string;
  query_type: 'pandas' | 'ai';
  description: string;
}

interface ScreeningState {
  loading: boolean;
  error: string | null;
  tickers: string[];
  tickerData: Record<string, any>;
}

type TimeHorizon = 'Short Term' | 'Mid Term' | 'Long Term';

interface FavoritesState {
  [key: string]: boolean;
}

const ScreenerManager: React.FC<ScreenerManagerProps> = ({ profileId, profile}) => {
  const [screeners, setScreeners] = useState<Screener[]>([]);
  const [precannedScreeners, setPrecannedScreeners] = useState<PrecannedScreener[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newScreenerName, setNewScreenerName] = useState('');
  const [selectedRule, setSelectedRule] = useState('');
  const [screeningStates, setScreeningStates] = useState<Record<string, ScreeningState>>({});
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [screenerToDelete, setScreenerToDelete] = useState<string | null>(null);
  const [screenerTimeHorizons, setScreenerTimeHorizons] = useState<Record<string, TimeHorizon>>({});
  const navigate = useNavigate();
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [tickerDetailData, setTickerDetailData] = useState<any>(null);
  const [favorites, setFavorites] = useState<FavoritesState>({});
  const [screenerColumnPreferences, setScreenerColumnPreferences] = useState<Record<string, string>>({});
  
  // Use auth context
  const { getAuthHeaders } = useAuth();
  const { 
    profiles, 
    currentProfile, 
    currentProfileId, 
    refreshProfiles, 
    handleProfileSelect 
  } = useProfile();  

  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  const fetchTickerData = useCallback(async (tickers: string[]) => {
    if (tickers.length === 0) return {};

    const uniqueTickers = [...new Set(tickers)];
    const newTickerData = await api.getMultiTickerData(uniqueTickers, currentProfile?.investment_profile_template || null);
    const uTickersData = uniqueTickers.map(ticker => newTickerData[ticker] || null).filter(ticker => ticker !== null);
    console.log('Fetched ticker data:', uTickersData); 
    return uTickersData;
  }, [profileId, api, currentProfile]);

  const loadPreferences = useCallback(() => {
    try {
      // Load column preferences
      const savedColumnPrefs = localStorage.getItem(`screener_column_prefs_${profileId}`);
      if (savedColumnPrefs) {
        setScreenerColumnPreferences(JSON.parse(savedColumnPrefs));
      }
      
      // Load time horizon preferences
      const savedTimeHorizons = localStorage.getItem(`screener_time_horizons_${profileId}`);
      if (savedTimeHorizons) {
        setScreenerTimeHorizons(JSON.parse(savedTimeHorizons));
      }
    } catch (err) {
      console.error('Error loading screener preferences:', err);
    }
  }, [profileId]);

  const saveColumnPreference = useCallback((screenerId: string, presetView: string) => {
    try {
      const updatedPreferences = {
        ...screenerColumnPreferences,
        [screenerId]: presetView
      };
      setScreenerColumnPreferences(updatedPreferences);
      localStorage.setItem(`screener_column_prefs_${profileId}`, JSON.stringify(updatedPreferences));
      console.log('Saved column preference:', { screenerId, presetView });
    } catch (err) {
      console.error('Error saving screener preferences:', err);
    }
  }, [screenerColumnPreferences, profileId]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    loadPreferences();
  }, [profileId, loadPreferences]);

  useEffect(() => {
    const initializeScreeners = async () => {
      try {
        if (!profileId) {
          throw new Error('Missing required configuration');
        }

        setLoading(true);
        setError(null);
        
        // First get user's screeners
        const fetchedScreeners = await api.getScreeners(profileId);
        console.log('Fetched user screeners:', fetchedScreeners);
        
        // Initialize favorites state with proper type
        const initialFavorites: FavoritesState = fetchedScreeners.reduce((acc, screener) => {
          const isFavorite = Boolean(screener.favorite);
          console.log(`Screener ${screener.name} (${screener.id}) favorite status:`, 
            { raw: screener.favorite, converted: isFavorite });
          return {
            ...acc,
            [screener.id]: isFavorite
          };
        }, {} as FavoritesState);
        
        console.log('Initial favorites state:', initialFavorites);
        
        // Set the favorites state
        setFavorites(initialFavorites);
        console.log('Favorites state set to:', initialFavorites);
        
        // Initialize screeners with empty data arrays
        const initializedScreeners = fetchedScreeners.map(screener => ({
          ...screener,
          data: []
        }));
        
        setScreeners(initializedScreeners);

        // Then get pre-canned screeners (for the create dialog)
        try {
          const precanned = await api.getPreCannedScreeners();
          setPrecannedScreeners(precanned);
        } catch (precannedError) {
          console.error('Error fetching pre-canned screeners:', precannedError);
        }

        // Finally, perform screening for each screener
        for (const screener of initializedScreeners) {
          try {
            const tickers = await api.performScreening(
              profileId,
              screener.name,
              screener.query_rule
            );

            if (Array.isArray(tickers) && tickers.length > 0) {
              // Fetch ticker data
              const tickerData = await fetchTickerData(tickers);
              
              // Update screener with data
              setScreeners(prev => prev.map(s => 
                s.id === screener.id 
                  ? { ...s, data: Object.values(tickerData || []) }
                  : s
              ));
            }
          } catch (screeningError) {
            console.error(`Error screening for ${screener.name}:`, screeningError);
          }
        }

        console.log('Default screener status:', fetchedScreeners.find(
          s => s.name === 'Profile Default Screener'
        ));

      } catch (err) {
        console.error('Error in initializeScreeners:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize screeners');
      } finally {
        setLoading(false);
      }
    };

    initializeScreeners();
  }, [api, profileId, fetchTickerData]);

  const handleCreateScreener = async (name: string, queryRule: string) => {
    try {
      setLoading(true);
      
      // Add pandas: prefix here when sending to the API
      const fullQueryRule = `pandas:${queryRule}`;
      
      console.log('Creating screener with:', {
        profile_id: profileId,
        name,
        query_rule: fullQueryRule
      });

      // Create the screener with the full query rule
      const nnScreener = await api.createScreener({
        profile_id: profileId,
        name: name,
        query_rule: fullQueryRule
      });

      console.log('Created screener:', nnScreener);
      let newScreener: Screener = { 
        profile_id: profileId,
        name: name,
        query_rule: fullQueryRule,
        favorite: false,
        id: name.toLowerCase(),
        data: []
      };

      // Add to screeners list with empty data initially
      setScreeners(prev => [...prev, {...newScreener, data: [] }]);

      // Close dialog and reset form immediately
      setIsCreateDialogOpen(false);
      setNewScreenerName('');
      setSelectedRule('');

      // Wait a bit longer before performing the initial screening
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Now perform the initial screening
      try {
        const results = await api.performScreening(
          profileId,
          newScreener.name,
          fullQueryRule
        );

        console.log('Screening results:', results);

        if (Array.isArray(results) && results.length > 0) {
          const tickerData = await fetchTickerData(results);
          
          // Update the screener with the results
          setScreeners(prev => prev.map(s => 
            s.id === newScreener.id 
              ? { ...s, data: Object.values(tickerData || []) }
              : s
          ));
        }
      } catch (screeningError) {
        console.error('Error performing initial screening:', screeningError);
        setError('Screener created but initial screening failed. Please try refreshing.');
      }

    } catch (err) {
      console.error('Error creating screener:', err);
      setError(err instanceof Error ? err.message : 'Failed to create screener');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScreener = async () => {
    if (!screenerToDelete) return;

    try {
      setLoading(true);
      await api.deleteScreener(profileId, screenerToDelete);
      setScreeners(screeners.filter(s => s.id !== screenerToDelete));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete screener');
    } finally {
      setLoading(false);
      setConfirmDeleteDialogOpen(false);
      setScreenerToDelete(null);
    }
  };

  const handleTickerSelect = async (ticker: string) => {
    try {
      const data = await api.getTickerData(ticker, currentProfile?.investment_profile_template || null);
      console.log('Fetched ticker data:', ticker, data);
      setTickerDetailData(data);
      setSelectedTicker(ticker);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data');
    }
  };

  const handleDelete = async (screenerId: string) => {
    setScreenerToDelete(screenerId);
    setConfirmDeleteDialogOpen(true);
  };

  const handleToggleFavorite = async (screener: Screener) => {
    try {
      // Convert current state to boolean explicitly
      const currentState = Boolean(favorites[screener.id]);
      const newFavoriteState = !currentState;
      
      console.log('Updating screener favorite:', {
        screenerId: screener.id,
        screenName: screener.name,
        currentStateRaw: favorites[screener.id],
        currentStateBoolean: currentState,
        newState: newFavoriteState
      });

      // Optimistic update
      setFavorites(prev => {
        const updatedFavorites = {
          ...prev,
          [screener.id]: newFavoriteState
        };
        console.log('Updated favorites state:', updatedFavorites);
        return updatedFavorites;
      });

      // Create minimal update payload with only required fields
      const updatePayload = {
        id: screener.id,
        profile_id: profileId,
        name: screener.name,
        query_rule: screener.query_rule,
        favorite: newFavoriteState
      };

      console.log('Sending update payload:', updatePayload);

      // Update on backend
      await api.updateScreener(updatePayload);
      console.log('Successfully updated favorite status on backend');

    } catch (err) {
      console.error('Error updating favorite status:', err);
      
      // Revert on error
      setFavorites(prev => {
        const revertedState = {
          ...prev,
          [screener.id]: !prev[screener.id]
        };
        console.log('Reverting to previous state due to error:', revertedState);
        return revertedState;
      });
      setError('Failed to update favorite status');
    }
  };

  const getActions = (screener: Screener) => {
    // Log the favorite status for debugging
    const isFavorite = Boolean(favorites[screener.id]);
    console.log(`Getting actions for ${screener.name} (${screener.id}):`, {
      favoritesState: favorites,
      thisScreenerFavoriteStatus: favorites[screener.id],
      convertedToBoolean: isFavorite
    });
    
    return [
      { 
        icon: isFavorite ? <StarIcon color="primary" /> : <StarBorderIcon />,
        label: isFavorite ? 'Remove from Favorites' : 'Add to Favorites',
        onClick: () => handleToggleFavorite(screener)
      },
      { icon: <DeleteIcon />, label: 'Delete', onClick: () => handleDelete(screener.id) }
    ];
  };

  const handleTimeHorizonChange = useCallback((screenerId: string, horizon: TimeHorizon) => {
    setScreenerTimeHorizons(prev => {
      const updated = {
        ...prev,
        [screenerId]: horizon
      };
      // Save to localStorage
      localStorage.setItem(`screener_time_horizons_${profileId}`, JSON.stringify(updated));
      return updated;
    });
  }, [profileId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  console.log('Screeners:', screeners);
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Screeners</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          CREATE SCREENER
        </Button>
      </Box>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(800px, 1fr))',
        gap: 2
      }}>
        {screeners.map((screener) => {
          // Add defensive check for screener.data
          const stocks = Array.isArray(screener.data) ? screener.data : [];
          
          // Get the saved column preference for this screener, or use default
          const savedPresetView = screenerColumnPreferences[screener.id] || 'Default View';
          
          // Extract the rule without the "pandas:" prefix
          const screeningRule = screener.query_rule?.replace('pandas:', '') || '';
          
          // Use EnhancedWatchlistTable with subtitle
          return (
            <EnhancedWatchlistTable
              key={`enhanced-screener-${screener.id}`}
              title={`${screener.name} (${stocks.length})`}
              subtitle={screeningRule}
              stocks={stocks}
              onTickerSelect={handleTickerSelect}
              actions={getActions(screener)}
              timeHorizon={screenerTimeHorizons[screener.id] || 'Short Term'}
              onTimeHorizonChange={(horizon: TimeHorizon) => handleTimeHorizonChange(screener.id, horizon)}
              initialPresetView={savedPresetView}
              onPresetViewChange={(presetView: string) => {
                saveColumnPreference(screener.id, presetView);
              }}
            />
          );
        })}
      </Box>

      <Dialog 
        open={isCreateDialogOpen} 
        onClose={() => setIsCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Screener</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Screener Name"
            fullWidth
            value={newScreenerName}
            onChange={(e) => setNewScreenerName(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth>
            <InputLabel>Select Screener Rule</InputLabel>
            <Select
              value={selectedRule}
              onChange={(e) => {
                const value = e.target.value;
                console.log('Selected rule:', value);
                // Don't add pandas: prefix here - use the value directly
                setSelectedRule(value);
              }}
            >
              {precannedScreeners.map((rule) => (
                <MenuItem 
                  key={rule.query_name} 
                  value={rule.query_name} // Use the query_name directly
                  disabled={rule.query_type === 'ai'}
                >
                  {rule.query_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleCreateScreener(newScreenerName, selectedRule)}
            variant="contained"
            disabled={!newScreenerName || !selectedRule}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteDialogOpen}
        title="Delete Screener"
        message="Are you sure you want to delete this screener?"
        onConfirm={handleDeleteScreener}
        onCancel={() => setConfirmDeleteDialogOpen(false)}
      />

      {/* Ticker Detail Dialog */}
      <Dialog
        open={!!selectedTicker}
        maxWidth="md"
        fullWidth
        onClose={() => setSelectedTicker(null)}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={() => setSelectedTicker(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {tickerDetailData && (
            <TickerDetailView 
              tickerInfo={tickerDetailData}
              ticker={selectedTicker!}
              onBack={() => setSelectedTicker(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ScreenerManager;
